import { useLingui } from "@lingui/react/macro";
import { autorun } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { NativeSelect } from "../../../components/ui-kit/base/native-select";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { SimplePlotModel } from "../../../models/plotting";
import styles from "./plot.module.css";

/**
 * Creates a plot model once and keeps its available plot-type options in sync
 * with the underlying data, which loads asynchronously after mount.
 */
export function usePlot<M extends SimplePlotModel>(factory: () => M): M {
  const [model] = useState(factory);

  useEffect(() => autorun(() => model.initPlot()), [model]);

  return model;
}

/** Plot type and date-range controls, meant for a table's filter toolbar. */
export const PlotControls = observer(function PlotControls(props: {
  model: SimplePlotModel;
}) {
  const { t } = useLingui();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <NativeSelect
        className="w-auto"
        value={props.model.plotType ?? ""}
        onChange={(event) => {
          props.model.plotType = event.target.value;
          props.model.preparePlot();
        }}
      >
        <option value="" disabled>
          {t`Select plot type`}
        </option>
        {props.model.plotTypeOptions.map((type) => (
          <option key={type}>{type}</option>
        ))}
      </NativeSelect>
      <DateInput
        type="datetime-local"
        value={props.model.plotStart ?? ""}
        onChange={(newValue) => {
          props.model.plotStart = newValue ?? "";
          props.model.updatePlot(props.model.plotStart, props.model.plotEnd);
        }}
      />
      <span>–</span>
      <DateInput
        type="datetime-local"
        value={props.model.plotEnd ?? ""}
        onChange={(newValue) => {
          props.model.plotEnd = newValue ?? "";
          props.model.updatePlot(props.model.plotStart, props.model.plotEnd);
        }}
      />
    </div>
  );
});

/** Canvas the plot renders into; hidden until a plot type is selected. */
export function PlotCanvas(props: { model: SimplePlotModel }) {
  return (
    <canvas
      className={styles.plot}
      id={props.model.chartId}
      style={{ display: "none" }}
    />
  );
}
